import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useStaticQuery, graphql } from 'gatsby'
import Img, { FluidObject } from 'gatsby-image'

import { Container } from 'components/Container'
import { backgroundColors, colors } from 'styles/colors'
import { displayWidth } from 'styles/width'
import { mobileAfterBorder } from 'styles/mobileAfterBorder'
import { Title, TitleH1 } from 'components/TitleComponent'
import { SliderComponent } from 'components/SliderComponent'
import { indent } from 'styles/indent'
import FullScreen from 'assets/icons/fullScreen.svg'
import { ModalCarousel } from 'components/ModalCarousel'
import { ProjectData } from 'layout/Project'
import { sendEvent } from 'tracking'
import { headerBg } from 'styles/headerBg'
import Slider from 'react-slick'

import { RunningLine } from 'components/RunningLine'
import { LeftSidebar, RightSidebar } from 'components/SideBar'

const ExampleOfProjectWrapper = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    background-color: ${backgroundColors.index};
    position: relative;
    border-bottom: 1px solid ${colors.dark};
    ${mobileAfterBorder}
    :before {
        ${headerBg}
    }
`

const TitleStyled = styled(Title)`
    @media (min-width: ${displayWidth.tablet}) {
        margin-left: 0;
        margin-right: 0;
        font-size: 20px;
    }
`
const TitleStyledH1 = styled(TitleH1)`
    display: flex;
    justify-content: center;
    margin-top: 8px;
    @media (min-width: ${displayWidth.tablet}) {
        margin-top: 32px;
    }
`
const SubTitle = styled.h3`
    font-weight: normal;
    font-size: 16px;
    line-height: 26px;
    text-align: center;
    letter-spacing: 0.4px;
    color: ${colors.dark};
    margin-bottom: 32px;
    @media (min-width: ${displayWidth.tablet}) {
        text-align: left;
    }
`
const HeroColumn = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 ${indent.heroColumnTablet} 24px;
    border-bottom: 1px solid ${colors.dark};
    @media (min-width: ${displayWidth.tablet}) {
        border-bottom: none;
        border-right: 1px solid ${colors.dark};
    }
    @media (min-width: ${displayWidth.desktop}) {
        justify-content: space-between;
        padding: 0 ${indent.heroColumnDesktop} 24px;
    }
`

const ImgStyled = styled(Img)<{ fluid: FluidObject }>`
    width: 100%;
    height: 100%;
`

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    background-color: ${backgroundColors.contact};
    position: relative;
`
const WrapperMobile = styled(Wrapper)`
    margin: 0 16px;
    @media (min-width: ${displayWidth.tablet}) {
        display: none;
    }
`
const WrapperDesktop = styled(Wrapper)`
    display: none;
    @media (min-width: ${displayWidth.tablet}) {
        display: flex;
        ::last-child {
            margin-bottom: 30px;
        }
    }
`
const FullScreenButton = styled(FullScreen)`
    fill: ${colors.white};
    stroke: ${colors.dark};
    stroke-width: 1px;
    position: absolute;
    top: 10px;
    right: 26px;
    width: 50px;
    height: 50px;
    cursor: pointer;
    z-index: 3;
    display: none;
    @media (min-width: ${displayWidth.tablet}) {
        display: block;
    }
    @media (min-width: ${displayWidth.desktop}) {
        right: 10px;
    }
`
// const Video = styled.div`
//     width: 100%;
//     margin: 0 16px;
//     position: relative;
//     padding-top: 53%; /* Player ratio: 100 / (1280 / 720) */
//     div {
//         position: absolute;
//         top: 0;
//         left: 0px;

//         width: 100%;
//         height: 100%;
//     }
//     @media (min-width: ${displayWidth.tablet}) {
//         margin: 0;
//         padding-top: 56.25%; /* Player ratio: 100 / (1280 / 720) */
//     }
// `

export const ExamplesOfProjectsViza = () => {
    const { t } = useTranslation()
    const [isModalOpen, setModalIsOpen] = useState(false)
    const [currentSlideS, setCurrentSlideS] = useState(0)

    const data = useStaticQuery(graphql`
        query {
            desktop: allFile(
                filter: { relativeDirectory: { eq: "projectExampleViza" } }
                sort: { fields: absolutePath }
            ) {
                edges {
                    node {
                        id
                        childImageSharp {
                            fluid(srcSetBreakpoints: [800], quality: 100) {
                                ...GatsbyImageSharpFluid
                            }
                            parent {
                                ... on File {
                                    name
                                }
                            }
                        }
                    }
                }
            }
        }
    `)

    const sliderSettings = {
        infinite: true,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    arrows: false,
                    dots: false,
                },
            },
        ],
    }
    const sliderRefD = useRef<Slider | null>(null)
    const sliderRefM = useRef<Slider | null>(null)
    const sliderD = sliderRefD.current

    return (
        <>
            <ExampleOfProjectWrapper>
                <LeftSidebar background={backgroundColors.index} />
                <Container>
                    <TitleStyledH1>{t('vizaHeader')}</TitleStyledH1>
                </Container>

                <RightSidebar />
            </ExampleOfProjectWrapper>
            <ExampleOfProjectWrapper>
                <LeftSidebar background={backgroundColors.index} />
                <Container columns={'1fr'} tabletColumns={'1fr 2fr'}>
                    <HeroColumn>
                        <TitleStyled>{t('staticImages')}</TitleStyled>
                        <SubTitle>{t('rendersTitle')}</SubTitle>
                        <p></p>
                    </HeroColumn>
                    <WrapperDesktop>
                        <FullScreenButton
                            onClick={() => {
                                setModalIsOpen(true)
                                sendEvent('FullScreen', {
                                    eventCategory: 'SliderExample3d',
                                    placement: 'ExampleOfProject3d',
                                })
                            }}
                        />
                        <SliderComponent
                            {...sliderSettings}
                            forwardRef={sliderRefD}
                            afterChange={(current: number) => {
                                setCurrentSlideS(current)
                                sendEvent('ShowSlide', {
                                    eventCategory: 'SliderExample3d',
                                    currentSlide: `${current}`,
                                    placement: 'ExampleOfProject3d',
                                })
                            }}
                        >
                            {data.desktop.edges.map(
                                (
                                    item: {
                                        node: {
                                            childImageSharp: {
                                                fluid: FluidObject
                                                parent: { name: string }
                                            }
                                        }
                                    },
                                    index: number
                                ) => {
                                    return (
                                        <ImgStyled
                                            key={index}
                                            fluid={
                                                item.node.childImageSharp.fluid
                                            }
                                            imgStyle={{
                                                objectFit: 'containe',
                                            }}
                                            alt={
                                                item.node.childImageSharp.parent
                                                    .name
                                            }
                                            title={
                                                item.node.childImageSharp.parent
                                                    .name
                                            }
                                        />
                                    )
                                }
                            )}
                        </SliderComponent>
                    </WrapperDesktop>
                    <WrapperMobile>
                        <SliderComponent
                            {...sliderSettings}
                            forwardRef={sliderRefM}
                            afterChange={(current: number) => {
                                setCurrentSlideS(current)
                                sendEvent('ShowSlide', {
                                    eventCategory: 'SliderExample3d',
                                    currentSlide: `${current + 1}`,
                                    placement: 'ExampleOfProject3d',
                                })
                            }}
                        >
                            {data.desktop.edges.map(
                                (
                                    item: {
                                        node: {
                                            childImageSharp: {
                                                fluid: FluidObject
                                                parent: { name: string }
                                            }
                                        }
                                    },
                                    index: number
                                ) => {
                                    return (
                                        <ImgStyled
                                            key={index}
                                            fluid={
                                                item.node.childImageSharp.fluid
                                            }
                                            imgStyle={{
                                                objectFit: 'containe',
                                            }}
                                            alt={
                                                item.node.childImageSharp.parent
                                                    .name
                                            }
                                            title={
                                                item.node.childImageSharp.parent
                                                    .name
                                            }
                                        />
                                    )
                                }
                            )}
                        </SliderComponent>
                    </WrapperMobile>
                </Container>
                <RightSidebar />
                <ModalCarousel
                    data={data.desktop.edges.map(
                        ({ node }: { node: ProjectData }) => node
                    )}
                    isModalOpen={isModalOpen}
                    setCurrentSlideS={setCurrentSlideS}
                    closeHandler={() => {
                        setModalIsOpen(false)
                        sliderD !== null && sliderD.slickGoTo(currentSlideS)
                    }}
                    initialSlideIndex={currentSlideS}
                />
            </ExampleOfProjectWrapper>
            <RunningLine>{t('designProject99')}</RunningLine>
            <ExampleOfProjectWrapper>
                <LeftSidebar background={backgroundColors.index} />
                <Container columns={'1fr'} tabletColumns={'1fr 2fr'}>
                    <HeroColumn>
                        <TitleStyled>{t('3Dvisualization360')}</TitleStyled>
                        <SubTitle>{t('visualization360title')}</SubTitle>
                        <p></p>
                    </HeroColumn>
                    <WrapperDesktop>
                        <iframe
                            id="inlineFrameExample"
                            title="Inline Frame Example"
                            width="100%"
                            height="500px"
                            allowFullScreen
                            src="https://3d.freedesign.in.ua/gk_centralny_4127-v4.html"
                            scrolling="no"
                        ></iframe>
                        {/*   <Video>
                     
                        <ReactPlayer
                                width="100%"
                                height="100%"
                                controls={false}
                                url={
                                    'https://youtube-nocookie.com/embed/sRXUXkpy5tY'
                                }
                                loop={true}
                                volume={0.5}
                                muted={true}
                                onStart={() =>
                                    sendEvent('Click', {
                                        eventCategory: 'video360',
                                    })
                                }
                                config={{
                                    youtube: {
                                        playerVars: {
                                            showinfo: 0,
                                            enablejsapi: 0,
                                            rel: 0,
                                            ecver: 2,
                                        },
                                    },
                                }}
                            /> 
                        </Video> */}
                    </WrapperDesktop>
                    <WrapperMobile>
                        <iframe
                            id="inlineFrameExample"
                            title="Inline Frame Example"
                            width="100%"
                            height="500px"
                            allowFullScreen
                            src="https://3d.freedesign.in.ua/gk_centralny_4127-v4.html"
                            scrolling="no"
                        ></iframe>
                        {/* <Video>
                            <ReactPlayer
                                width="100%"
                                height="100%"
                                controls={false}
                                url={
                                    'https://youtube-nocookie.com/embed/sRXUXkpy5tY'
                                }
                                loop={true}
                                volume={0.5}
                                muted={true}
                                onStart={() =>
                                    sendEvent('Click', {
                                        eventCategory: 'video360',
                                    })
                                }
                                config={{
                                    youtube: {
                                        playerVars: {
                                            showinfo: 0,
                                            enablejsapi: 0,
                                            rel: 0,
                                            ecver: 2,
                                        },
                                    },
                                }}
                            />
                        </Video> */}
                    </WrapperMobile>
                </Container>
                <RightSidebar />
            </ExampleOfProjectWrapper>
        </>
    )
}
